import React from "react";
import AccordionSummary from "@mui/material/AccordionSummary";
import {
  Accordion,
  IconButton,
  Card,
  Typography,
  Tooltip,
  Link,
  Avatar,
} from "@mui/material";
import Div from "@jumbo/shared/Div";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

const CategoryItem = ({
  item,
  disabled,
  onDelete,
  onEdit,
  defaultLanguage,
  categories,
  searchName,
}) => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const handleCopyCode = (e) => {
    e.stopPropagation();
    const code = item?._id;
    if (!code) return;
    navigator.clipboard
      .writeText(String(code))
      .then(() => {
        enqueueSnackbar(
          t("pages.dsokCategories.codeCopied", {
            defaultValue: "Kód kategórie skopírovaný do schránky",
          }),
          { variant: "success" }
        );
      })
      .catch(() => {
        enqueueSnackbar(t("notifications.wasFailed"), { variant: "error" });
      });
  };

  const toSvgDataUrl = (icon) => {
    if (!icon) return "";
    const payload =
      typeof icon === "object"
        ? icon.base64 || icon.svg || icon.data || ""
        : icon;
    if (!payload) return "";
    if (typeof payload === "string" && payload.startsWith("data:"))
      return payload;
    if (typeof payload === "string" && payload.trim().startsWith("<svg")) {
      const encoded = btoa(unescape(encodeURIComponent(payload)));
      return `data:image/svg+xml;base64,${encoded}`;
    }
    return `data:image/svg+xml;base64,${payload}`;
  };

  return (
    <Card sx={{ mb: 1, ml: searchName ? 0 : item.level * 5 }}>
      <Accordion expanded={false} square sx={{ borderRadius: 2 }}>
        <AccordionSummary
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          sx={{
            px: 3,
            flexDirection: "row-reverse",
            "& .MuiAccordionSummary-content": {
              alignItems: "center",
              justifyContent: "space-between",

              "&.Mui-expanded": {
                margin: "12px 0",
              },
            },
            ".MuiAccordionSummary-expandIconWrapper": {
              color: "text.secondary",
              transform: "none",
              height: 28,
              width: 28,
              alignItems: "center",
              justifyContent: "center",
              mr: 1,

              "&.Mui-expanded": {
                transform: "none",
                color: "primary.main",
                borderColor: "primary.main",
              },

              "& svg": {
                fontSize: "1.25rem",
              },
            },
          }}
        >
          {/* Icon first */}
          <Div
            sx={{
              flex: { xs: "0 0 auto", md: 0 },
              px: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Avatar
              src={
                item?.customIcon
                  ? typeof item.customIcon === "string"
                    ? item.customIcon
                    : ""
                  : item?.icon
                  ? toSvgDataUrl(item.icon)
                  : "/images/noImagePlaceholder.png"
              }
              variant="rounded"
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                bgcolor: item?.customIcon ? "#C0844D" : "#fff",
              }}
            />
          </Div>

          {/* Name second */}
          <Div sx={{ flex: { xs: "0 1 auto", md: 2 }, flexShrink: 0, px: 1 }}>
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.presentation.label")}
            </Typography>
            <Div>
              <Typography variant={"body1"}>
                <Link onClick={onEdit}>
                  {(item?.name &&
                    item?.name[defaultLanguage || i18n.language]) ||
                    ""}
                </Link>
              </Typography>
              {(item?.customCategoryId || item?.menuType) && (
                <Div
                  sx={{ mt: 0.25, display: "flex", flexWrap: "wrap", gap: 1 }}
                >
                  {item?.customCategoryId && (
                    <Typography
                      variant={"caption"}
                      color="text.secondary"
                      sx={{ fontSize: "0.7rem", lineHeight: 1.2 }}
                    >
                      Custom ID: {item.customCategoryId}
                    </Typography>
                  )}
                  {item?.menuType && (
                    <Typography
                      variant={"caption"}
                      color="text.secondary"
                      sx={{ fontSize: "0.7rem", lineHeight: 1.2 }}
                    >
                      Menu type: {item.menuType}
                    </Typography>
                  )}
                </Div>
              )}
            </Div>
          </Div>

          {/* Category Code third */}
          <Div sx={{ flex: { xs: "0 1 auto", md: 2 }, flexShrink: 0, px: 1 }}>
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.dsokCategories.categoryCode")}
            </Typography>
            <Tooltip
              title={t("pages.dsokCategories.clickToCopy", {
                defaultValue: "Kliknutím skopírovať",
              })}
              placement="top-start"
            >
              <Typography
                variant={"body1"}
                onClick={handleCopyCode}
                sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
              >
                {item?._id}
              </Typography>
            </Tooltip>
          </Div>

          {/* Status fourth */}
          <Div sx={{ flex: { xs: "0 1 auto", md: 1 }, flexShrink: 0, px: 1 }}>
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pos.status", { defaultValue: "Status" })}
            </Typography>
            <Typography
              variant={"body1"}
              color={item?.isActive ? "success.main" : "error.main"}
            >
              {item?.isActive ? t("common.active") : t("common.inactive")}
            </Typography>
          </Div>

          {/* Actions last */}
          <Div
            sx={{
              flex: { xs: "0 2 auto", md: 0 },
              px: 1,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.presentation.actions")}
            </Typography>
            <Div
              sx={{
                display: "flex",
                flex: { direction: "row", flexWrap: "wrap" },
                minWidth: 70,
              }}
            >
              <Div>
                <IconButton
                  size="small"
                  variant={"contained"}
                  disabled={disabled}
                  onClick={onEdit}
                >
                  <Tooltip title={t("buttons.edit")} placement="top-start">
                    <EditIcon />
                  </Tooltip>
                </IconButton>
                <IconButton
                  size="small"
                  variant={"contained"}
                  disabled={disabled}
                  onClick={() => onDelete(item)}
                >
                  <Tooltip title={t("buttons.delete")} placement="top-start">
                    <DeleteForeverIcon />
                  </Tooltip>
                </IconButton>
              </Div>
            </Div>
          </Div>
        </AccordionSummary>
      </Accordion>
    </Card>
  );
};
/* Todo item prop define */
export default CategoryItem;
